import { PaletteMode, ThemeOptions } from '@mui/material'
import colors from '../constants/colors'

export const TAG_COLORS = {
  tagBoxSuccess: {
    border: colors.TAG_BORDER_BLUE,
    text: colors.TAG_BLUE,
    background: colors.TAG_LIGHT_BLUE,
  },
  tagBoxError: {
    border: colors.TAG_RED,
    text: colors.TAG_RED,
    background: colors.TAG_LIGHT_RED,
  },
  tagBoxMaterial: {
    border: colors.TAG_PURPLE,
    text: colors.TAG_PURPLE,
    background: colors.TAG_LIGHT_PURPLE,
  },
  tagBoxArchive: {
    border: colors.TAG_LINES_GRAY,
    text: colors.BLACK,
    background: colors.TAG_GRAY_FILL,
  },
}

/**
 * Material UI theme config for "Light Mode"
 */
export const LIGHT_THEME = {
  palette: {
    mode: 'light' as PaletteMode,
    background: {
      paper: colors.TAG_MAIN_BACKGROUND,
      default: colors.WHITE,
    },
    action: {
      active: '#a2a3a2',
    },
    ...TAG_COLORS,
  },
}

/**
 * Material UI theme config for "Dark Mode"
 */
export const DARK_THEME = {
  palette: {
    mode: 'dark' as PaletteMode,
    background: {
      paper: colors.TAG_MAIN_BACKGROUND,
      default: colors.BLACK,
    },
    action: {
      active: '#757575',
    },
  },
}

const typographyConstants = {
  htmlFontSize: 10,
  fontFamily: 'Aktiv Grotesk, sans-serif',
  boldText45: {
    fontSize: '4.5rem',
    fontWeight: 700,
    lineHeight: '5rem',
  },
  boldTextXlarge: {
    fontSize: '2.4rem',
    fontWeight: 700,
    lineHeight: '3rem',
  },
  textXlarge: {
    fontSize: '2.4rem',
    fontWeight: 400,
    lineHeight: '3rem',
  },
  boldTextLarge: {
    fontSize: '1.8rem',
    fontWeight: 700,
    lineHeight: '2.4rem',
  },
  textLarge: {
    fontSize: '1.8rem',
    fontWeight: 400,
    lineHeight: '2.4rem',
  },
  boldTextSmall: {
    fontSize: '1.2rem',
    fontWeight: 700,
    lineHeight: '1.6rem',
  },
  textSmall: {
    fontSize: '1.2rem',
    fontWeight: 400,
    lineHeight: '1.6rem',
  },
  textMedium: {
    fontSize: '1.4rem',
    fontWeight: 400,
    lineHeight: '2rem',
  },
  boldTextMedium: {
    fontSize: '1.4rem',
    fontWeight: 700,
    lineHeight: '2rem',
  },
  boldText16: {
    fontSize: '1.6rem',
    fontWeight: 700,
    lineHeight: '1.6rem',
  },
  text16: {
    fontSize: '1.6rem',
    fontWeight: 400,
    lineHeight: '1.6rem',
  },
  text32: {
    fontSize: '3.2rem',
    fontWeight: 400,
    lineHeight: '3.6rem',
  },
  text32Bold: {
    fontSize: '3.2rem',
    fontWeight: 700,
    lineHeight: '3.6rem',
  },
  bodyBold: {
    fontSize: '1.4rem',
    fontWeight: 700,
    lineHeight: '2rem',
    '@media (min-width:1440px)': {
      fontSize: '1.4rem',
      fontWeight: 700,
      lineHeight: '2rem',
    },
    '@media (min-width:1920px)': {
      fontSize: '1.8rem',
      fontWeight: 700,
      lineHeight: '2.4rem',
    },
  },
  bodyRegular: {
    fontSize: '1.4rem',
    fontWeight: 400,
    lineHeight: '2rem',
    '@media (min-width:1440px)': {
      fontSize: '1.4rem',
      fontWeight: 400,
      lineHeight: '2rem',
    },
    '@media (min-width:1920px)': {
      fontSize: '1.8rem',
      fontWeight: 400,
      lineHeight: '2.4rem',
    },
  },
}

export const typography: ThemeOptions = {
  typography: {
    ...typographyConstants,

    h1: {
      ...typographyConstants.boldTextLarge,
      '@media (min-width:1440px)': typographyConstants.boldTextXlarge,
      '@media (min-width:1920px)': typographyConstants.text32Bold,
    },
    h2: {
      ...typographyConstants.textLarge,
      '@media (min-width:1440px)': typographyConstants.textXlarge,
      '@media (min-width:1920px)': typographyConstants.text32,
    },
    h3: {
      ...typographyConstants.boldText16,
      '@media (min-width:1440px)': typographyConstants.boldTextLarge,
      '@media (min-width:1920px)': typographyConstants.boldTextXlarge,
    },
    h4: {
      ...typographyConstants.text16,
      '@media (min-width:1440px)': typographyConstants.textLarge,
      '@media (min-width:1920px)': typographyConstants.textXlarge,
    },
    h5: {
      ...typographyConstants.textMedium,
      '@media (min-width:1440px)': typographyConstants.textMedium,
      '@media (min-width:1920px)': typographyConstants.textLarge,
    },
    bodyBold: typographyConstants.bodyBold,
    bodyRegular: typographyConstants.bodyRegular,
    labelBold: typographyConstants.boldTextSmall,
    labelRegular: typographyConstants.textSmall,
  },
}

export const components: ThemeOptions = {
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          height: '86px',
          width: '100%',
          backgroundColor: colors.WHITE,
          padding: '18px 36px',
          boxSizing: 'border-box',
          borderBottom: `2px solid ${colors.TAG_LINES_GRAY}`,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'row',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: '0 !important',
          },
          '&:after': {
            borderBottom: '0 !important',
          },
        },
        input: {
          backgroundColor: colors.TAG_GRAY_FILL,
          borderRadius: '5px',
          padding: '8px',
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.TAG_LINES_GRAY,
          '& .MuiSvgIcon-root': { fontSize: 21 },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
      variants: [
        {
          props: { variant: 'submitFormButton' },
          style: {
            textTransform: 'none',
            width: '100%',
            backgroundColor: colors.TAG_LIGHT_BLUE,
            border: `1px solid ${colors.TAG_BORDER_BLUE}`,
            color: colors.TAG_BLUE,
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            color: colors.BLACK,
            width: 'fit-content',
            borderRadius: '1rem',
            border: '1px solid black !important',
            '& .MuiButtonBase-root': {
              border: '1px solid black !important',
            },
            '&:disabled': {
              color: colors.TAG_LINES_GRAY,
              borderColor: `${colors.TAG_LINES_GRAY} !important`,
            },
          },
        },
        {
          props: { variant: 'dashed' },
          style: {
            color: colors.TAG_TEXT_GRAY,
            backgroundColor: 'transparent',
            width: 'fit-content',
            border: `1px dashed ${colors.TAG_LINES_GRAY} !important`,
            '& .MuiButtonBase-root': {
              border: `1px dashed ${colors.TAG_LINES_GRAY} !important`,
            },
          },
        },
      ],
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.TAG_BLUE,
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 'unset !important',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {},
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fill: colors.TAG_TEXT_GRAY,
          padding: '1rem 3rem',
          '&.Mui-selected, &.Mui-selected:hover': {
            color: colors.WHITE,
            backgroundColor: colors.BLACK,
            fill: colors.WHITE,
          },
        },
      },
    },
  },
}
